import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, AlertCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

export default function MFAPage() {
  const navigate = useNavigate()
  const { verifyMFA, loading, error, clearError, mfaToken } = useAuth()

  // If no MFA token (expired or never set), redirect to login
  if (!mfaToken) {
    navigate('/login', { replace: true })
    return null
  }
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const refs = useRef<(HTMLInputElement | null)[]>([])

  // Auto-focus first input on mount
  useEffect(() => { refs.current[0]?.focus() }, [])

  const handleChange = (i: number, val: string) => {
    // Handle multi-character paste into a single input (some browsers)
    if (val.length > 1) {
      handlePasteString(val)
      return
    }
    if (!/^[0-9]?$/.test(val)) return
    const next = [...digits]
    next[i] = val
    setDigits(next)
    if (val && i < 5) refs.current[i + 1]?.focus()
    // Auto-submit when all digits filled
    if (val && i === 5) {
      next[5] = val
      if (next.every(d => d)) {
        setTimeout(() => {
          const code = next.join('')
          clearError()
          verifyMFA(code).then(() => navigate('/app')).catch(() => {
            setDigits(['', '', '', '', '', ''])
            refs.current[0]?.focus()
          })
        }, 50)
      }
    }
  }

  const handlePasteString = (raw: string) => {
    const cleaned = raw.replace(/\D/g, '').slice(0, 6)
    if (!cleaned) return
    const next = ['', '', '', '', '', '']
    for (let j = 0; j < cleaned.length; j++) {
      next[j] = cleaned[j]
    }
    setDigits(next)
    // Focus the next empty box, or last box if all filled
    const nextEmpty = next.findIndex(d => !d)
    refs.current[nextEmpty >= 0 ? nextEmpty : 5]?.focus()
    // Auto-submit if 6 digits pasted
    if (cleaned.length === 6) {
      setTimeout(() => {
        clearError()
        verifyMFA(cleaned).then(() => navigate('/app')).catch(() => {
          setDigits(['', '', '', '', '', ''])
          refs.current[0]?.focus()
        })
      }, 100)
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    handlePasteString(pasted)
  }

  const handleKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (digits.some(d => !d)) return
    clearError()
    try {
      await verifyMFA(digits.join(''))
      navigate('/app')
    } catch {
      // Error is already set in the auth store; clear digits for retry
      setDigits(['', '', '', '', '', ''])
      refs.current[0]?.focus()
    }
  }

  const complete = digits.every(d => d !== '')

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-xl bg-grad-blue flex items-center justify-center">
                <Shield size={28} className="text-white" aria-hidden />
              </div>
            </div>
            <p className="font-display text-sm font-bold tracking-wide text-text-secondary mb-1">AgentVault</p>
            <h1 className="font-display text-2xl font-bold text-text-primary">Two-Factor Authentication</h1>
            <p className="text-text-secondary text-sm mt-2">
              Enter your authenticator code to access the trading terminal
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-rose/10 border border-rose/20 text-rose-light text-sm"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* OTP inputs */}
            <div
              className="flex gap-2 justify-center mb-8"
              role="group"
              aria-label="One-time passcode"
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => (refs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  aria-label={`Digit ${i + 1}`}
                  className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold bg-surface-card border border-border
                             rounded-xl text-text-primary caret-blue
                             focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent
                             transition-colors duration-150"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={!complete || loading}
              className="btn-primary w-full py-3 text-base"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden />
                  Verifying…
                </span>
              ) : (
                'Verify & Enter'
              )}
            </button>

            <button
              type="button"
              className="w-full mt-3 text-sm text-text-secondary hover:text-text-primary transition-colors py-2"
            >
              Resend code
            </button>
          </form>

          <div className="mt-6 flex items-start gap-3 p-3 bg-surface rounded-lg border border-border">
            <span className="text-blue text-lg" aria-hidden>ℹ</span>
            <p className="text-xs text-text-secondary">
              Enter the code from your authenticator app (Google Authenticator, Authy, etc.)
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
